// ================================================================
// Ziffern vereinheitlichen — persisch/arabisch getippt, lateinisch gespeichert
// ================================================================
// Wer auf einer persischen oder arabischen Tastatur tippt, bekommt NICHT die
// Ziffern 0–9, sondern ۰۱۲۳۴۵۶۷۸۹ (persisch) bzw. ٠١٢٣٤٥٦٧٨٩ (arabisch).
// Sie SEHEN aus wie Zahlen und sind für den Rechner keine:
//
//   parseFloat('۱۲۳')            → NaN     (Menge/Preis werden still zu nichts)
//   '۰۷۹۱۲۳۴۵۶۷'.replace(/\D/g,'') → ''    (die Telefonnummer verschwindet GANZ,
//                                           der WhatsApp-Link zeigt ins Leere)
//   /^[0-9]+$/.test('۱۲۳')       → false   (jede Prüfung schlägt fehl)
//
// Das Tückische daran: nichts stürzt ab. Der Preis wird 0, die Nummer leer —
// und niemand merkt es, bis beim Kunden eine Offerte über CHF 0.00 liegt.
//
// Deshalb wird an EINER Stelle normalisiert, nicht in 79 Formularen: ein
// Lauscher in der Aufnahmephase (installDigitNormalizer) fasst jede Eingabe der
// App an, bevor React sie sieht. Wer ein neues Feld baut, muss nichts wissen.

// U+06F0–U+06F9 = persisch, U+0660–U+0669 = arabisch-indisch.
const PERSIAN_ZERO = 0x06f0;
const ARABIC_ZERO = 0x0660;

/**
 * Persische/arabische Ziffern → 0–9. Alles andere bleibt Zeichen für Zeichen
 * unverändert — der Text «۲ عدد کاناپه» wird zu «2 عدد کاناپه», nicht zu
 * lateinischer Umschrift.
 *
 * Auch das arabische Dezimalzeichen ٫ wird zum Punkt: sonst wäre «۱۲٫۵» nach
 * der Ziffern-Umstellung «12٫5» — immer noch NaN. Das Tausendertrennzeichen ٬
 * fällt weg, wie es auch bei einem lateinischen Apostroph nötig wäre.
 */
export function toAsciiDigits(value) {
  if (typeof value !== 'string' || !value) return value;
  let out = '';
  let changed = false;
  for (const ch of value) {
    const code = ch.codePointAt(0);
    if (code >= PERSIAN_ZERO && code <= PERSIAN_ZERO + 9) {
      out += String(code - PERSIAN_ZERO);
      changed = true;
    } else if (code >= ARABIC_ZERO && code <= ARABIC_ZERO + 9) {
      out += String(code - ARABIC_ZERO);
      changed = true;
    } else if (ch === '٫') { // ٫ arabisches Dezimalzeichen
      out += '.';
      changed = true;
    } else if (ch === '٬') { // ٬ arabisches Tausendertrennzeichen
      changed = true; // ersatzlos weg
    } else {
      out += ch;
    }
  }
  // Unverändertes Original zurückgeben: der Aufrufer kann so billig prüfen,
  // ob überhaupt etwas passiert ist (Identitätsvergleich).
  return changed ? out : value;
}

/** Enthält der Text überhaupt fremde Ziffern? Billiger Vortest. */
export function hasNonAsciiDigits(value) {
  return typeof value === 'string' && /[۰-۹٠-٩٫٬]/.test(value);
}

// Felder, die NICHT angefasst werden dürfen.
// Passwörter: ein Passwort mit persischen Ziffern ist genau diese Zeichenfolge —
// stillschweigend umgeschrieben käme der Nutzer nie wieder hinein.
function isExempt(el) {
  if (!el || (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA')) return true;
  const type = (el.getAttribute('type') || '').toLowerCase();
  if (type === 'password') return true;
  // Ausdrücklicher Ausstieg für Sonderfälle, die es später geben mag.
  if (el.dataset && el.dataset.keepDigits !== undefined) return true;
  return false;
}

// Wert setzen, ohne Reacts value-Tracker mitzuziehen (Begründung siehe unten
// bei installDigitNormalizer). Fällt auf die einfache Zuweisung zurück, falls
// ein Browser den Prototyp-Setter nicht hergibt.
function setValueBypassingReact(el, value) {
  const proto = el.tagName === 'TEXTAREA' ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  const nativeSetter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (nativeSetter) nativeSetter.call(el, value);
  else el.value = value;
}

/**
 * Einmal beim Start aufrufen.
 *
 * AUFNAHMEPHASE auf `document`: der Lauscher läuft, bevor das Ereignis den
 * React-Wurzelknoten erreicht. React liest `event.target.value` erst dort —
 * also sieht React bereits die lateinischen Ziffern, und der Zustand der App
 * kann gar nicht erst mit fremden Ziffern gefüllt werden. Ein Lauscher in der
 * Blasenphase käme zu spät.
 *
 * DIE FALLE, die beim Bauen zuschlug: ein schlichtes `el.value = …` macht die
 * Eingabe für React UNSICHTBAR. React hängt an jedes Feld einen «value tracker»
 * (eine eigene Eigenschaft auf dem Knoten) und feuert `onChange` nur, wenn der
 * gemerkte Wert vom aktuellen abweicht. Schreibt man über diese Eigenschaft,
 * wird der Tracker mitgezogen — React vergleicht danach zwei gleiche Werte,
 * hält das für «nichts passiert» und ruft `onChange` NIE auf. Das Feld sähe
 * richtig aus, der Zustand der App bliebe leer. Gemessen: DOM «0791234567»,
 * React-Zustand «».
 *
 * Deshalb wird der Setter des Prototyps benutzt. Er umgeht die Eigenschaft auf
 * dem Knoten, der Tracker behält den alten Wert, React sieht einen Unterschied
 * und ruft `onChange` mit den lateinischen Ziffern auf.
 *
 * Der Cursor muss von Hand zurückgesetzt werden: eine Zuweisung an `.value`
 * schiebt ihn ans Ende. Die Ersetzung ist Zeichen für Zeichen (nur ٬ fällt
 * weg), deshalb genügt die alte Position — ausser hinter einem entfernten ٬,
 * dort rückt sie um eins nach vorn.
 */
export function installDigitNormalizer(target = document) {
  target.addEventListener(
    'input',
    (event) => {
      const el = event.target;
      if (isExempt(el)) return;
      const before = el.value;
      if (!hasNonAsciiDigits(before)) return;
      const after = toAsciiDigits(before);
      if (after === before) return;

      const start = el.selectionStart;
      const end = el.selectionEnd;
      const removed = before.length - after.length; // nur durch ٬
      setValueBypassingReact(el, after);
      // `type="email"`/`number` erlauben keine Cursor-Abfrage — dort wirft das
      // Setzen. Der Wert stimmt trotzdem, nur der Cursor springt ans Ende.
      try {
        if (start !== null) el.setSelectionRange(Math.max(0, start - removed), Math.max(0, end - removed));
      } catch {
        /* Feldtyp ohne Cursor-Steuerung — nicht schlimm */
      }
    },
    true, // ← Aufnahmephase
  );
}
